import HTMLTemplate from './template.html'
import './styles.scss'
import tippy, { followCursor } from 'tippy.js';
import 'tippy.js/dist/tippy.css' // optional for styling
import 'tippy.js/animations/shift-away.css'
import { debounce } from 'lodash'
import MapBox from '../../modules/map'
import { getAirportsFeatureCollection, updateMapAirports, updateMapRiskLayer } from '../../services/airport'
import { getFilteredAirports } from '../../services/AirportService'
import { DetailsPanel } from '../DetailsPanel/DetailsPanel'
import MixPanelService from '../../services/MixPanelService'
import Cities from '../../modules/cities'
import InternationalSOSAlerts from '../../modules/InternationalSOSAlerts'
import AvsecAlerts from '../../modules/avsecAlerts'
import Tails from '../../modules/tails'
import { ModalComponent } from '../Modal/Modal'
import AeroSpace from '../../modules/aerospace'
import { FIR_DETAILS } from '../../data/fir-details'
import moment from 'moment'
import 'moment-timezone'
import { COUNTRY_DETAILS } from '../../data/country-details'
import CountryRisk from '../../modules/countryRisk'
import { AlertsBar } from './../../components/AlertsBar/AlertsBar'
import Ports from '../../modules/ports';
import Hotspots from '../../modules/hotspots'
import User from '../../modules/user'
import MaritimeAlerts from '../../modules/maritimeAlerts'
import { EventFilter } from '../EventFilter/EventFilter'

export const SearchBar = (() => {
    const guessedTimeZone = moment.tz(moment.tz.guess()).format('z')
    const vueComponent = {
        data() {
            return {
                searchInput: '',
                searchResults: {
                    airports: [],
                    FIRs: [],
                    countries: [],
                    Cities: [],
                    ports: [],
                    alerts: []
                },
                enableAviationSearchAndFunctionalities: false,
                enableCityLayers: false,
                enableMedicalRiskLayers: false,
                enableTravelRiskLayers: false,
                enableCountries: false, // Most of the module don't use it, so invert it and keep disabled by default
                enableCities: false,
                isLoadingResults: false,
                activeSearchResultsPanel: 'airports', // FIRs, alerts, countries
                airports: [],
                showMapAirports: true,
                showMapTails: window.sessionStorage.getItem('showMapTails') === 'true' ? true : false,
                showMapLayers: false,
                mapLayerLabel: 'Map Layers',
                showMapLayersDropdownFilters: false,
                showTravelRiskLayers: false,
                showMedicalRiskLayers: false,
                activeCountryLayer: 'travel', // travel, medical
                showFIRLayers: false,
                showCityLayers: false,
                showPortLayers: true,
                showHotSpotLayers: false,
                showAirportDropdownFilters: false,
                internationalAirportsFilter: true,
                domesticAirportsFilter: false,
                militaryAirportsFilter: false,
                civilianAirportsFilter: true,
                jointAirportsFilter: true,
                privateAirportsFilter: true,
                airportsRiskRating: 'airport', // 'country', 'city', 'airport',
                showCountryRiskLayers: true,
                enableAdditionalLayers: false,

                // UI helper classes
                disabledTravelRiskToggle: true,
                disableCityToggle: true,
                hasLoadedFIRModule: false,
                showMobileDropdownFilters: false,
                riskIntelligence: true,
                userPermission: [],

                hasLoadedAlerts: false,
                displayAlerts: false,
                activeDropDown: '',
                alertFilters: {},
                enableAvsecAlerts: false,
                enableAviationFeatures: true,
                isComprehensiveCrewSupport: false,
                isMedicalOnlyUser: false
            }
        },
        created() {
            AeroSpace.subscribeToModuleInitialization().then(() => this.hasLoadedFIRModule = true)
            
            User.getUserPermissions().then(permissions => {
                this.userPermission = permissions
                this.riskIntelligence = this.userPermission.includes('luxury_yachting') ? true : false
                this.isComprehensiveCrewSupport = this.userPermission.includes('comprehensive_crew_support') && this.userPermission.indexOf('basic') === -1 ? true : false
                this.isMedicalOnlyUser = this.userPermission.includes('medical') && this.userPermission.indexOf('basic') === -1 ? true : false
                if (this.isMedicalOnlyUser || this.isComprehensiveCrewSupport) {
                    this.enableAviationFeatures = false
                }
            })

            InternationalSOSAlerts.getCurrentAlertsAvailableCategories().then(alertFilters => {
                this.alertFilters = alertFilters
                window.setTimeout(() => this.hasLoadedAlerts = true, 1000)
            })

            User.getLocalUserInfo().then(user => {
                const userFilters = JSON.parse(user.settings.filters)
                this.displayAlerts = userFilters.enableAlerts
                this.enableAvsecAlerts = userFilters.enableAviationAlerts
            })

            // Hide top dropdowns if user clicks anywhere outside
            document.body.addEventListener('click', (event) => {
                if (event.target.closest('.js-ignore-child-target') === null) {
                    this.showMobileDropdownFilters = false
                    this.activeDropDown = ''
                    this.activeDropDownFirst = ''
                }
            })
        },
        methods: {
            debounceInput: debounce(function () {
                this.search()
            }, 1500),
            search() {
                let searchValue = this.searchInput.toLowerCase().trim()
                if (searchValue.includes(' and ')) searchValue = searchValue.replace(' and ', ' & ')
                const eventPromises = []

                let filteredAirports = getFilteredAirports(this.searchInput, this.airports)
                let filteredFIRs = []
                let filteredCountries = []
                let filteredCities = []
                let filteredAlerts = []
                let filteredPorts = []

                if (this.enableAviationSearchAndFunctionalities) {
                    // Search FIR Layers
                    for (const FIRCode in FIR_DETAILS) {
                        const FIRDetails = FIR_DETAILS[FIRCode]

                        if (FIRCode.toLowerCase().includes(searchValue) ||
                            FIRDetails.name.toLowerCase().includes(searchValue) ||
                            FIRDetails.controllingCountryCode.toLowerCase().includes(searchValue) ||
                            FIRDetails.controllingCountryName.toLowerCase().includes(searchValue) ||
                            (FIRDetails.controllingCountryName + ' Airspace').toLowerCase().includes(searchValue)
                        ) {
                            filteredFIRs.push({
                                code: FIRCode,
                                ...FIRDetails
                            })
                        }
                    }
                }

                if (this.enableCountries) {
                    // Search Countries
                    COUNTRY_DETAILS.forEach(country => {
                        if (country.name.toLowerCase().includes(searchValue) ||
                            country.ISO_3166_1_ALPHA_2.toLowerCase().includes(searchValue) ||
                            (country.ISO_3166_1_ALPHA_3 !== null && country.ISO_3166_1_ALPHA_3.toLowerCase().includes(searchValue))
                        ) {
                            filteredCountries.push(country)
                        }
                    })
                }

                const citiesData = Cities.getCities()
                if (searchValue) {
                    citiesData.forEach(city => {
                        if (city.cityName.toLowerCase().includes(searchValue)) {
                            filteredCities.push(city)
                        }
                    })
                }
                if (this.riskIntelligence) {
                    const portsData = Ports.getPorts()
                    if (searchValue) {
                        portsData.forEach(port => {
                            if (port?.portName?.toLowerCase()?.includes(searchValue)) {
                                filteredPorts.push(port)
                            }
                        })
                    }

                    const maritimeAlertsData = MaritimeAlerts.getMaritimeAlerts()
                    if (searchValue) {
                        maritimeAlertsData.forEach(alerts => {
                            if (alerts?.title?.toLowerCase()?.includes(searchValue) ||
                                alerts?.area?.name?.toLowerCase()?.includes(searchValue) ||
                                alerts?.entity?.name?.toLowerCase()?.includes(searchValue) ||
                                alerts?.region?.name?.toLowerCase()?.includes(searchValue)
                            ) {
                                const countryName = alerts?.entity?.name ?? ''
                                const areaName = alerts?.area?.name ?? ''
                                const regionName = alerts?.region?.name ?? ''
                                const names = [regionName, areaName, countryName].filter(name => name !== '')
                                const location = names.join(', ')
                                filteredAlerts.push({
                                    ...alerts,
                                    id: alerts.id,
                                    isInternationalSOSAlert: false,
                                    isAviationAlert: false,
                                    isMaritimeAlert: true,
                                    displayDate: this.getDisplayDateTime(alert.date),
                                    heading: alerts.title,
                                    location: location
                                })
                            }
                        })
                    }
                }


                eventPromises.push(InternationalSOSAlerts.getModuleLatestAlerts())

                if (this.enableAviationSearchAndFunctionalities) {
                    eventPromises.push(AvsecAlerts.getModuleLatestAlerts())
                }

                Promise.all(eventPromises).then(values => {
                    // Search International SOS Alerts
                    values[0].forEach(alert => {
                        if (alert.Title.toLowerCase().includes(searchValue) ||
                            alert.TitleLocation.toLowerCase().includes(searchValue)) {
                            filteredAlerts.push({
                                id: alert.Id,
                                isInternationalSOSAlert: true,
                                isAviationAlert: false,
                                displayDate: this.getDisplayDateTime(alert.ActiveFrom),
                                heading: alert.Title,
                                location: alert.location
                            })
                        }
                    })

                    if (this.enableAviationSearchAndFunctionalities) {
                        // Search Aviation Alerts
                        values[1].forEach(alert => {
                            if (alert.Heading.toLowerCase().includes(searchValue) ||
                                alert.SelectedCountry.toLowerCase().includes(searchValue)) {
                                filteredAlerts.push({
                                    id: alert.NodeId,
                                    isoCode: alert.CountriesAffected?.[0]?.CountryCodeAlpha2,
                                    isInternationalSOSAlert: false,
                                    isAviationAlert: true,
                                    displayDate: this.getDisplayDateTime(alert.ActiveFrom),
                                    heading: alert.Heading,
                                    location: alert.SelectedCountry
                                })
                            }
                        })
                        this.activeSearchResultsPanel = 'airports'
                    } else {
                        this.activeSearchResultsPanel = 'alerts'
                    }

                    this.searchResults.airports = filteredAirports
                    this.searchResults.FIRs = filteredFIRs
                    this.searchResults.countries = filteredCountries
                    this.searchResults.cities = filteredCities
                    this.searchResults.alerts = filteredAlerts
                    if (this.riskIntelligence) {
                        this.searchResults.ports = filteredPorts
                    }

                    this.isLoadingResults = false
                })
            },
            toggleDropDown(dropdownID) {
                this.activeDropDown = this.activeDropDown === dropdownID ? '' : dropdownID
                // @adam : commented for now as it is causing the deopdown to close when intracting on small screens
                // this.showMobileDropdownFilters = false
            },
            openAirport(airportDetails) {
                MapBox.map.jumpTo({
                    center: [airportDetails.longitude, airportDetails.latitude],
                    zoom: 8
                })

                // Show details panel and hide the search
                DetailsPanel.showDetailsPanel(airportDetails, 'airport')
                this.searchInput = ''

                MixPanelService.track('Airport Search', {
                    'Airport Name': airportDetails.airportName,
                    'Airport Details': `${airportDetails.icao}${(airportDetails.iata !== '' ? ' | ' + airportDetails.iata : '')} | ${airportDetails.cityName}, ${airportDetails.country}`
                })
            },
            openFIR(FIRdetails) {
                AeroSpace.selectFIR(FIRdetails.code, FIRdetails.controllingCountryCode, FIRdetails.name, FIRdetails.countries)
                this.searchInput = ''
            },
            openCountry(country) {
                CountryRisk.showCountry(country.ISO_3166_1_ALPHA_2, country.ISO_3166_1_ALPHA_3, country.name)
                this.searchInput = ''
            },
            openCity(city) {
                Cities.showCity(city)
                this.searchInput = ''
            },
            openPort(port) {
                Ports.showPort(port)
                this.searchInput = ''
            },
            openAlert(alert, isAviationAlert) {
                if (isAviationAlert) {
                    AvsecAlerts.showAlert(alert.id)
                } else if (alert.isInternationalSOSAlert) {
                    InternationalSOSAlerts.showAlert(alert.id)
                } else if (alert.isMaritimeAlert) {
                    MaritimeAlerts.showAlert(alert)
                }
            },
            changeAirportFilters(label, value) {
                MixPanelService.track('Airports toggle', {
                    label: label,
                    value: value ? 'Visible' : 'Hidden'
                })
                const filteredAirportsFeatureCollection = getAirportsFeatureCollection(this.airports,
                    this.internationalAirportsFilter,
                    this.domesticAirportsFilter,
                    this.militaryAirportsFilter,
                    this.civilianAirportsFilter,
                    this.jointAirportsFilter,
                    this.privateAirportsFilter)

                updateMapAirports(filteredAirportsFeatureCollection)
            },
            changeMapRiskType(type) {
                MixPanelService.track('Airports risk/rating toggle', {
                    value: type
                })
                updateMapRiskLayer(this.airportsRiskRating)
            },
            addAirportToWatchlist(airportICAO) {
                ModalComponent.$data.airportCodeToAdd = airportICAO
                ModalComponent.$data.showWatchlistModal = true
                ModalComponent.$data.addToWatchFromLocation = 'Search Bar'
            },
            hideFilters() {
                this.searchInput = ''
                this.showAirportDropdownFilters = false
                this.showMapLayersDropdownFilters = false
                this.showMobileDropdownFilters = false
            },
            getDisplayDateTime(timestamp) {
                return moment(timestamp).format('DD MMM YYYY HH:mm') + ' ' + guessedTimeZone
            },
            getDisplayDate(UTCDate) {
                return moment(UTCDate).format('DD MMM YYYY') + ' ' + guessedTimeZone
            },
            showTravelRiskLayersUI() {
                CountryRisk.hideMedicalRiskLayers()
                CountryRisk.showTravelRiskLayers()
            },
            showMedicalRiskLayersUI() {
                CountryRisk.hideTravelRiskLayers()
                CountryRisk.showMedicalRiskLayers()
            },
            toggleMobileDropdownFilters() {
                this.showMobileDropdownFilters = !this.showMobileDropdownFilters
                AlertsBar.activeDropDown = ''
                AlertsBar.activeDropDownFirst = ''
            },
            debounceUpdateUserSettings: debounce(function () {
                this.updateUserSettings()
            }, 500),
            updateUserSettings() {
                User.getLocalUserSettings().then(settings => {
                    let updatedUserSettings = settings
                    let userFilters = JSON.parse(settings.filters)
                    userFilters.enableAviationAlerts = this.enableAvsecAlerts

                    // Update also main categories to see if they were enabled or not
                    for (const key in this.alertFilters) {
                        if (userFilters.alertFilters[key] !== undefined) {
                            userFilters.alertFilters[key].enabled = this.alertFilters[key].enabled
                        }
                    }

                    updatedUserSettings.filters = JSON.stringify(userFilters)
                    User.updateUserSettings(updatedUserSettings)
                })
            },
            showFilters() {
                EventFilter.$data.enableAvsecAlerts = this.enableAvsecAlerts
                EventFilter.$data.enableAlerts = this.displayAlerts
                EventFilter.$data.alertFilters = this.alertFilters
                EventFilter.$data.enableAviationFeatures = this.enableAviationFeatures

                EventFilter.show()
            },
            filterAlerts(type) {
                const alertMapping = {
                    Security: {
                        name: 'Security Alerts',
                        enabled: this.alertFilters?.Security?.enabled
                    },
                    Medical: {
                        name: 'Medical Alerts',
                        enabled: this.alertFilters?.Medical?.enabled
                    },
                    Aviation: {
                        name: 'Aviation Alerts',
                        enabled: this.enableAvsecAlerts
                    }
                }

                const { name, enabled } = alertMapping[type]
                this.alertToggleTracking(name, enabled)

                InternationalSOSAlerts.filter(this.alertFilters, EventFilter.$data.timeFrame, EventFilter.$data.alertSeverities)

                if (this.displayAlerts) {
                    if (this.enableAviationFeatures) {
                        this.enableAvsecAlerts ? AvsecAlerts.show() : AvsecAlerts.hide()
                    }

                    this.debounceUpdateUserSettings()
                } else {
                    // If the alerts in general are disabled, it's fine, but we still want to save user preference on that in his settings
                    this.debounceUpdateUserSettings()
                }
            },
            alertToggleTracking(label, value) {
                MixPanelService.track('Alerts toggle', {
                    label: label,
                    value: value ? 'Visible' : 'Hidden'
                })
            }
        },
        watch: {
            searchInput() {
                this.isLoadingResults = this.searchInput.trim() !== ''
                this.debounceInput()
            },
            showMapAirports() {
                let visibility = 'visible'
                if (!this.showMapAirports) visibility = 'none'

                MixPanelService.track('Airports view toggle', {
                    value: visibility === 'visible' ? 'Visible' : 'Hidden'
                })
                MapBox.map.setLayoutProperty('airports', 'visibility', visibility)
                MapBox.map.setLayoutProperty('airports-points', 'visibility', visibility)
                MapBox.map.setLayoutProperty('clusters', 'visibility', visibility)
                MapBox.map.setLayoutProperty('cluster-count', 'visibility', visibility)
            },
            showMapTails() {
                MixPanelService.track('Map Tails view toggle', {
                    value: this.showMapTails ? 'Visible' : 'Hidden'
                })
                this.showMapTails ? Tails.show() : Tails.hide()
                window.sessionStorage.setItem('showMapTails', this.showMapTails)
            },
            // TODO@Adam: Since changing to a radio input value is not updating back to false after switching between options
            showFIRLayers() {
                MixPanelService.track('FIR Layer view toggle', {
                    value: this.showFIRLayers ? 'Visible' : 'Hidden'
                })
                this.showFIRLayers ? AeroSpace.showFIRLayers() : AeroSpace.hideFIRLayers()
            },
            showCityLayers() {
                this.showCityLayers ? Cities.show() : Cities.hide()
            },
            showPortLayers() {
                this.showPortLayers ? Ports.show() : Ports.hide()
            },
            showHotSpotLayers() {
                this.showHotSpotLayers ? Hotspots.show() : Hotspots.hide()
            },
            showTravelRiskLayers() {
                this.showTravelRiskLayers ? this.showTravelRiskLayersUI() : CountryRisk.hideTravelRiskLayers()
            },
            showMedicalRiskLayers() {
                this.showMedicalRiskLayers ? this.showMedicalRiskLayersUI() : CountryRisk.hideMedicalRiskLayers()
            },
            activeCountryLayer() {
                if (this.showCountryRiskLayers) {
                    if (this.activeCountryLayer === 'travel') {
                        this.showTravelRiskLayersUI()
                    } else if (this.activeCountryLayer === 'medical') {
                        this.showMedicalRiskLayersUI()
                    }
                }
            },
            showCountryRiskLayers() {
                if (this.showCountryRiskLayers) {
                    if (this.showTravelRiskLayers) {
                        this.showTravelRiskLayersUI()
                    } else if (this.showMedicalRiskLayers) {
                        this.showMedicalRiskLayersUI()
                    }

                    if (this.activeCountryLayer === 'travel') {
                        this.showTravelRiskLayersUI()
                    } else if (this.activeCountryLayer === 'medical') {
                        this.showMedicalRiskLayersUI()
                    }
                } else {
                    CountryRisk.hideTravelRiskLayers()
                    CountryRisk.hideMedicalRiskLayers()
                }
            },
            internationalAirportsFilter() { this.changeAirportFilters('International Airport', this.internationalAirportsFilter) },
            domesticAirportsFilter() { this.changeAirportFilters('Domestic Airport', this.domesticAirportsFilter) },
            militaryAirportsFilter() { this.changeAirportFilters('Military Airport', this.militaryAirportsFilter) },
            civilianAirportsFilter() { this.changeAirportFilters('Civilian Airport', this.civilianAirportsFilter) },
            jointAirportsFilter() { this.changeAirportFilters('Joint Airport', this.jointAirportsFilter) },
            privateAirportsFilter() { this.changeAirportFilters('Private Airport', this.privateAirportsFilter) },
            airportsRiskRating() { this.changeMapRiskType(`Risk Rating - ${this.airportsRiskRating}`) },
            'displayAlerts': function () {
                if (this.hasLoadedAlerts) {
                    if (this.displayAlerts) {
                        InternationalSOSAlerts.show()
                        if (this.enableAviationFeatures && this.enableAvsecAlerts) {
                            AvsecAlerts.show()
                        }
                    } else {
                        InternationalSOSAlerts.hide()
                        if (this.enableAviationFeatures) {
                            AvsecAlerts.hide()
                        }
                    }

                    MixPanelService.track('Alerts view toggle', {
                        value: this.displayAlerts ? 'Visible' : 'Hidden'
                    })
                }
            }
        },
        template: HTMLTemplate
    }

    const DOMElement = document.getElementById('vue-search-bar')
    if (DOMElement === null) {
        return null
    } else {
        return Vue.createApp(vueComponent).use({
            install: (app) => {
                app.directive('tooltip', {
                    beforeMount(el, binding) {
                        tippy(el, {
                            content: binding.value,
                            placement: 'bottom-start',
                            animation: 'shift-away',
                            allowHTML: true,
                            trigger: 'mouseenter click focus',
                            arrow: false,
                            followCursor: true,
                            plugins: [followCursor]
                        })
                    }
                })
            }
        }).mount(DOMElement)
    }
})()
